import { useState, useEffect } from 'react'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { invalidateCompanySettingsCache, type CompanySettings } from '../../lib/companySettings'

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'
const labelCls = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

const FIELDS: { key: keyof CompanySettings; label: string; textarea?: boolean }[] = [
  { key: 'legal_name', label: 'Legal Name' },
  { key: 'address_line_1', label: 'Address Line 1' },
  { key: 'address_line_2', label: 'Address Line 2' },
  { key: 'address_line_3', label: 'Address Line 3' },
  { key: 'postcode', label: 'Postcode' },
  { key: 'country', label: 'Country' },
  { key: 'phone', label: 'Phone' },
  { key: 'company_no', label: 'Company No.' },
  { key: 'bifa_line', label: 'BIFA / Trading Conditions Line', textarea: true },
  { key: 'logo_url', label: 'Logo URL (top-left of letterhead, ~130px wide)' },
  { key: 'bifa_logo_url', label: 'BIFA Membership Logo URL (footer)' },
  { key: 'vat_number', label: 'VAT Number' },
  { key: 'bank_name', label: 'Bank Name & Address', textarea: true },
  { key: 'bank_account_number', label: 'Bank Account Number' },
  { key: 'bank_sort_code', label: 'Bank Sort Code' },
  { key: 'bank_iban', label: 'Bank IBAN' },
  { key: 'bank_swift', label: 'Bank Swift/BIC' },
]

// The authorisation code that unlocks money edits on an issued invoice. Stored only as a bcrypt
// hash in invoice_security_settings (never readable from the browser) -- this card can set a new
// code, not display the current one.
function InvoiceAuthCodeCard() {
  const [isSet, setIsSet] = useState<boolean | null>(null)
  const [code, setCode] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => { supabase.rpc('invoice_edit_auth_code_is_set').then(({ data }) => setIsSet(!!data)) }, [])
  async function save() {
    setBusy(true); setMsg(null)
    const { error } = await supabase.rpc('set_invoice_edit_auth_code', { p_new_code: code })
    setBusy(false)
    if (error) { setMsg(`Error: ${error.message}`); return }
    setCode(''); setIsSet(true); setMsg('Authorisation code updated.')
  }
  return (
    <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-3">
      <h2 className="font-semibold text-gray-800 dark:text-white">Invoice edit authorisation code</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Required to change amounts, VAT rate, Bill To or charge lines on an issued invoice. Stored hashed; it cannot be viewed, only replaced.
        {isSet === false && <b className="text-red-600 dark:text-red-400"> No code is set — amount edits are blocked until you set one.</b>}
      </p>
      <div className="flex gap-3">
        <input type="password" autoComplete="new-password" className={inputCls} placeholder="New code (min 4 characters)" value={code} onChange={e => setCode(e.target.value)} />
        <button onClick={save} disabled={busy || code.trim().length < 4}
          className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">Set code</button>
      </div>
      {msg && <p className="text-xs text-gray-600 dark:text-gray-300">{msg}</p>}
    </div>
  )
}

export default function CompanySettingsPage() {
  const { userEmail } = useAuth()
  const [form, setForm] = useState<CompanySettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  useEffect(() => {
    supabase.from('company_settings').select('*').eq('id', 'default').maybeSingle().then(({ data }) => {
      setForm(data as CompanySettings)
      setLoading(false)
    })
  }, [])

  async function save() {
    if (!form) return
    setSaving(true)
    const { error } = await supabase.from('company_settings').update({ ...form, updated_by: userEmail }).eq('id', 'default')
    setSaving(false)
    if (error) { alert(`Error: ${error.message}`); return }
    invalidateCompanySettingsCache()
    setSavedAt(new Date().toLocaleTimeString('en-GB'))
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Company Settings</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          The single source every letterhead/PDF (Delivery Notes, Collection Notes, warehouse
          release notes) reads its company name, address, phone and BIFA registration text
          from — edit here rather than in code.
        </p>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400">Loading…</div>
      ) : !form ? (
        <div className="py-16 text-center text-gray-400">No company_settings row found — run the migration first.</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          {FIELDS.map(f => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}</label>
              {f.textarea ? (
                <textarea className={inputCls} rows={3} value={form[f.key] || ''}
                  onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
              ) : (
                <input className={inputCls} value={form[f.key] || ''}
                  onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
              )}
            </div>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <button onClick={save} disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
            {savedAt && <span className="text-xs text-green-600">Saved at {savedAt}</span>}
          </div>
        </div>
      )}
      <InvoiceAuthCodeCard />
    </div>
  )
}
